import * as React from "react";
import { ShieldAlert } from "lucide-react";

import {
  useLinkContractFamilyDocumentMutation,
  useMarkContractFamilyOrphanMutation,
  useOverrideContractFamilyRoleMutation,
  useUnlinkContractFamilyDocumentMutation,
  useUpdateContractFamilyMetadataMutation,
} from "@/api/contract-families";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  MatchCandidateResponse,
  MetadataPatchPayload,
  ReviewQueueItem,
  RoleUpdatePayload,
} from "@/types/contract-family";

import { AmbiguousReviewPanel } from "./AmbiguousReviewPanel";
import { MetadataEditorDialog } from "./MetadataEditorDialog";
import { ParentOption, RoleOverrideDialog } from "./RoleOverrideDialog";
import { deduplicateReviewCandidates, formatContractFamilyOutcomeReason } from "../contractFamilyViewModels";

interface Props {
  items: ReviewQueueItem[];
  selectedItemId: string | null;
  isLoading: boolean;
  isError: boolean;
  onSelectItem: (item: ReviewQueueItem) => void;
  onItemResolved?: (item: ReviewQueueItem) => void;
}

const candidateLabel = (candidate: MatchCandidateResponse) =>
  `${candidate.candidate_master_document_id.slice(0, 10)} (${Math.round(candidate.confidence * 100)}%)`;

const getParentOptions = (item: ReviewQueueItem | null): ParentOption[] =>
  item?.candidates.map(candidate => ({
    documentId: candidate.candidate_master_document_id,
    label: candidateLabel(candidate),
  })) ?? [];

export const ReviewQueue: React.FC<Props> = ({
  items,
  selectedItemId,
  isLoading,
  isError,
  onSelectItem,
  onItemResolved,
}) => {
  const [linkDocument] = useLinkContractFamilyDocumentMutation();
  const [markOrphan] = useMarkContractFamilyOrphanMutation();
  const [overrideRole] = useOverrideContractFamilyRoleMutation();
  const [unlinkDocument] = useUnlinkContractFamilyDocumentMutation();
  const [updateMetadata] = useUpdateContractFamilyMetadataMutation();
  const [pendingDocumentId, setPendingDocumentId] = React.useState<string | null>(null);
  const pendingDocumentIdRef = React.useRef<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [roleTarget, setRoleTarget] = React.useState<ReviewQueueItem | null>(null);
  const [metadataTarget, setMetadataTarget] = React.useState<ReviewQueueItem | null>(null);

  const normalizedItems = React.useMemo(
    () => items.map(item => ({ ...item, candidates: deduplicateReviewCandidates(item.candidates) })),
    [items],
  );
  const selectedItem = normalizedItems.find(item => item.document_id === selectedItemId) ?? normalizedItems[0] ?? null;

  const runAction = async (item: ReviewQueueItem, action: () => Promise<unknown>, resolveOnSuccess = true) => {
    if (pendingDocumentIdRef.current !== null) {
      return false;
    }

    pendingDocumentIdRef.current = item.document_id;
    setPendingDocumentId(item.document_id);
    setErrorMessage(null);

    try {
      await action();
      if (resolveOnSuccess) {
        onItemResolved?.(item);
      }
      return true;
    } catch {
      setErrorMessage("Action failed. Please try again.");
      return false;
    } finally {
      pendingDocumentIdRef.current = null;
      setPendingDocumentId(null);
    }
  };

  const handleAccept = (item: ReviewQueueItem, candidate: MatchCandidateResponse) =>
    runAction(item, () =>
      linkDocument({
        documentId: item.document_id,
        payload: {
          parent_document_id: candidate.candidate_master_document_id,
          comment: null,
        },
      }).unwrap(),
    );

  const handleOrphan = (item: ReviewQueueItem) =>
    runAction(item, () =>
      markOrphan({
        documentId: item.document_id,
        payload: {
          comment: null,
        },
      }).unwrap(),
    );

  const handleSendBackToReview = (item: ReviewQueueItem) =>
    runAction(item, () =>
      unlinkDocument({
        documentId: item.document_id,
        payload: {
          comment: null,
        },
      }).unwrap(),
    );

  const handleRoleSubmit = async (payload: RoleUpdatePayload) => {
    if (!roleTarget) {
      return;
    }

    const success = await runAction(roleTarget, () =>
      overrideRole({
        documentId: roleTarget.document_id,
        payload,
      }).unwrap(),
    );

    if (success) {
      setRoleTarget(null);
    }
  };

  const handleMetadataSubmit = async (payload: MetadataPatchPayload) => {
    if (!metadataTarget) {
      return;
    }
    const success = await runAction(
      metadataTarget,
      () => updateMetadata({ documentId: metadataTarget.document_id, payload }).unwrap(),
      false,
    );
    if (success) {
      setMetadataTarget(null);
    }
  };

  if (isLoading) {
    return <div className="px-4 py-8 text-sm text-slate-500">Loading review queue...</div>;
  }

  if (isError) {
    return <div className="px-4 py-8 text-sm text-red-700">Unable to load review queue.</div>;
  }

  if (normalizedItems.length === 0) {
    return <div className="px-4 py-8 text-sm text-slate-500">No documents need review.</div>;
  }

  return (
    <>
      <div className="grid min-h-full divide-y divide-slate-200 md:grid-cols-[280px_minmax(0,1fr)] md:divide-x md:divide-y-0">
        <aside className="min-h-0 overflow-auto bg-slate-50">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-950">Review Queue</h2>
            <p className="text-xs text-slate-500">{normalizedItems.length} pending documents</p>
          </div>
          <div className="divide-y divide-slate-100">
            {normalizedItems.map(item => {
              const selected = item.document_id === selectedItem?.document_id;
              const pending = pendingDocumentId === item.document_id;

              return (
                <button
                  key={item.document_id}
                  type="button"
                  className={cn(
                    "block w-full min-w-0 px-4 py-3 text-left hover:bg-white",
                    selected && "bg-blue-50 hover:bg-blue-50",
                  )}
                  aria-current={selected ? "true" : undefined}
                  onClick={() => onSelectItem(item)}
                >
                  <span className="block truncate text-sm font-medium text-slate-950">
                    {item.name?.trim() || item.document_id}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="px-1.5 py-0 text-[10px] uppercase">
                      {item.role}
                    </Badge>
                    <Badge variant="traffic-amber" className="px-1.5 py-0 text-[10px]">
                      {item.family_status}
                    </Badge>
                    {pending && <span className="text-[10px] text-blue-700">Saving...</span>}
                  </span>
                  <span className="mt-1 flex items-center gap-2 text-[10px] text-slate-500">
                    <span>
                      {item.candidates.length} {item.candidates.length === 1 ? "candidate" : "candidates"}
                    </span>
                    {item.hidden_candidate_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <ShieldAlert className="h-3 w-3" />
                        {item.hidden_candidate_count} hidden
                      </span>
                    )}
                  </span>
                  <span className="mt-1.5 block text-xs leading-4 text-slate-600">
                    {formatContractFamilyOutcomeReason(item.outcome_reason)}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <AmbiguousReviewPanel
          item={selectedItem}
          outcomeExplanation={selectedItem ? formatContractFamilyOutcomeReason(selectedItem.outcome_reason) : null}
          pending={pendingDocumentId !== null}
          errorMessage={errorMessage}
          onAcceptCandidate={handleAccept}
          onMarkOrphan={handleOrphan}
          onChangeRole={setRoleTarget}
          onEditMetadata={setMetadataTarget}
          onSendBackToReview={handleSendBackToReview}
        />
      </div>

      <RoleOverrideDialog
        open={!!roleTarget}
        target={
          roleTarget
            ? {
                documentId: roleTarget.document_id,
                name: roleTarget.name,
                role: roleTarget.role,
              }
            : null
        }
        parentOptions={getParentOptions(roleTarget ?? selectedItem)}
        isSubmitting={roleTarget ? pendingDocumentId === roleTarget.document_id : false}
        onOpenChange={open => {
          if (!open) {
            setRoleTarget(null);
          }
        }}
        onSubmit={handleRoleSubmit}
      />
      <MetadataEditorDialog
        open={!!metadataTarget}
        item={metadataTarget}
        isSubmitting={metadataTarget ? pendingDocumentId === metadataTarget.document_id : false}
        onOpenChange={open => {
          if (!open) {
            setMetadataTarget(null);
          }
        }}
        onSubmit={handleMetadataSubmit}
      />
    </>
  );
};
